import { useMemo } from "react";
import { type PassengerPricing, TripType } from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePriceDisplay } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./ReviewBreakdown.module.scss";
import { getFixedPricingFromFare } from "@/utilities/flights/fixedPricing";

export interface ReviewBreakdownComponentProps extends PlatformComponentProps {
  passengerPricing: PassengerPricing;
  tripType: TripType;
}

export const ReviewBreakdown = ({
  passengerPricing,
  tripType,
  className,
  children,
}: ReviewBreakdownComponentProps) => {
  const styles = useFlightStyles(
    CoreFlightsComponent.ReviewBreakdown,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.ReviewBreakdown
  );

  const { t } = useI18nContext();

  const subtitle = useMemo(() => {
    switch (tripType) {
      case TripType.one_way: {
        return t("oneWayTripCategoryPassenger");
      }
      case TripType.open_jaw: {
        return t("multiCityTripCategoryPassenger");
      }
      case TripType.round_trip: {
        return t("roundTripCategoryPassenger");
      }
    }
  }, [t, tripType]);

  const prices = getFixedPricingFromFare(passengerPricing);

  const { rewardsThenPriceText } = usePriceDisplay({
    prices,
    inline: "all",
  });

  return (
    <div className={clsx(block, className)}>
      <div className={cn("price")}>{rewardsThenPriceText}</div>
      <div className={cn("price-subtitle")}>{subtitle}</div>
      {children}
    </div>
  );
};
